<template>
    <div class="course__list">
        <div class="course__list_header">
            <view-actions
                :title="'Courses'"
                :cancel-button-active="false"
                :submit-button-text="'Create course'"
                :submit-button-active="user.isCourseCreator() && user.hasCustomerServices(['CCS'])"
                @emit-submit="createCourse"
            />
        </div>
        <template v-if="user.isCourseCreator() || user.isSuperadmin() || user.isLearningExperienceAdministrator()">
            <div class="course__list_tabs">
                <div class="container">
                    <div class="columns is-variable is-0">
                        <template v-if="user.isCourseCreator() && user.hasCustomerServices(['CCS'])">
                            <div class="column is-narrow">
                                <component-button
                                    :name="'course-list-view-course-creator'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'course-creator'"
                                    :secondary-theme="routeQueryView === 'course-creator' ? 'primary' : ''"
                                    @click="switchView('course-creator')"
                                >Course creator
                                </component-button>
                            </div>
                        </template>
                        <template v-if="user.isLearningExperienceAdministrator()">
                            <div class="column is-narrow">
                                <component-button
                                    :name="'course-list-view-admin'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'admin'"
                                    :secondary-theme="routeQueryView === 'admin' ? 'primary' : ''"
                                    @click="switchView('admin')"
                                >Admin view
                                </component-button>
                            </div>
                        </template>
                        <template v-if="user.isLearner()">
                            <div class="column is-narrow">
                                <component-button
                                    :name="'course-list-view-learner'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'learner'"
                                    :secondary-theme="routeQueryView === 'learner' ? 'primary' : ''"
                                    @click="switchView('learner')"
                                >Learner view
                                </component-button>
                            </div>
                        </template>
                        <div class="column"></div>
                    </div>
                </div>
            </div>
        </template>
        <div class="course__list_main">
            <component-list
                ref="componentlist"
                :key="rerenderIndex"
                :component-grid="CourseListGrid"
                :component-table="CourseListTable"
                :component-table-name="`courses-${routeQueryView}`"
                :list-view-items="['table', 'grid']"
                :empty-state-title="outputEmptyStateTitle"
                :filter-search-queryable="true"
                :filter-select-active="Boolean(filtersSelect.length)"
                :filter-select-items="filtersSelect"
                :filter-select-prefix="''"
                :get-pagination-getter="'getEbsCoursesPagination'"
                :get-items-getter="'getEbsCourses'"
                :get-items-dispatch="getDispatchName"
                :get-items-dispatch-params="routeQueryIds ? { ids: routeQueryIds } : undefined"
                :item-name="['course', 'courses']"
                :table-preferences-active="true"
                @emit-request-finally="handleEmitRequestFinally"
            />
        </div>
    </div>
</template>

<script>
import ViewActions from '@/components/default/shared/ViewActions'
import ComponentList from '@/components/default/shared/ComponentList'
import CourseListTable from '@/components/ebs/courses/list/CourseListTable'
import CourseListGrid from '@/components/ebs/courses/list/CourseListGrid'
import { User } from '@/User'
import ComponentButton from '@/components/default/shared/ComponentButton'

export default {
  name: 'course-list',
  components: { ComponentButton, ComponentList, ViewActions },
  data () {
    return {
      user: new User(),
      CourseListTable,
      CourseListGrid,
      rerenderIndex: 0
    }
  },
  computed: {
    routeModulesQuery () {
      return this.$route.query ? this.$route.query.modules : undefined
    },
    routeModulesQueryMapped () {
      const modulesRaw = this.routeModulesQuery
      const modulesMapped = modulesRaw ? modulesRaw.split(',') : []
      let modulesArr = []
    
      for (let module of modulesMapped) {
        modulesArr.push({
          label: module.split(';')[1],
          value: module.split(';')[0]
        })
      }
    
      return modulesArr
    },
    outputEmptyStateTitle () {
      let str

      switch (this.routeQueryView) {
        case 'course-creator':
          str = 'Courses created by your organisation will appear here. Go ahead and create one by clicking Create course.'
          break
        case 'admin':
          str = 'Published courses will appear here.'
          break
        case 'learner':
          str = 'Available courses will appear here.'
          break
      }

      return str
    },
    routeQueryView () {
      return this.$route.query.view
    },
    routeQueryIds () {
      return this.$route.query.ids
    },
    getDispatchName () {
      let dispatchName

      if (this.routeQueryView) {
        switch (this.routeQueryView) {
          case 'course-creator':
            dispatchName = 'ebsCoursesGet'
            break
          case 'admin':
            dispatchName = 'ebsCoursesGetAdmin'
            break
          case 'learner':
            dispatchName = 'ebsCoursesGetLearner'
            break
        }
      }

      return dispatchName
    },
    filtersSelect () {
      let filtersArr = []

      if (this.routeQueryView === 'course-creator' || this.routeQueryView === 'admin') {
        filtersArr.push({
          key: 'labels',
          label: 'Labels',
          type: 'multiselect',
          getItemsGetter: 'getEbsCourseCustomLabelsMappedIdNameStatus',
          getItemsGetterMapped: 'getEbsCourseCustomLabelsMappedLabelValue',
          getItemsDispatch: 'ebsCourseGetLabels',
          getPaginationGetter: 'getEbsCourseCustomLabelsPagination',
          itemName: ['label', 'labels'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        }),
        filtersArr.push({
          key: 'modules',
          label: 'Modules',
          type: 'multiselect',
          preselected: this.routeModulesQueryMapped,
          getItemsGetter: 'getEbsModulesMappedIdNameVersionLabels',
          getItemsGetterMapped: 'getEbsModulesMappedLabelValue',
          getItemsDispatch: 'ebsModulesGetSCORMReleased',
          getItemsDispatchParams: '&status=active,deleted',
          getPaginationGetter: 'getEbsModulesPagination',
          itemName: ['module', 'modules'],
          searchQueryable: true,
          tableLabels: [
            {
              key: 'name',
              title: 'Name',
              type: 'string',
              sortable: true,
              queryable: true
            }
          ]
        })
      }
      if (this.routeQueryView === 'course-creator') {
        filtersArr.push({
          key: 'status',
          label: 'Status',
          type: 'checkbox',
          values: [
            {
              label: 'Draft',
              value: 'draft'
            },
            {
              label: 'Active',
              value: 'active'
            }
          ]
        })
      }

      return filtersArr
    }
  },
  watch: {
    routeQueryView () {
      this.rerenderIndex++
    }
  },
  methods: {
    createCourse () {
      this.$router.push({ name: 'ebs-course-add' })
    },
    switchView (value) {
      this.$router.push({ query: { view: value, modules: this.routeModulesQuery } })
    },
    initializeRouteQuery () {
      if (!this.routeQueryView) {
        if (this.user.isCourseCreator() && this.user.hasCustomerServices(['CCS'])) {
          this.switchView('course-creator')
        } else if (this.user.isSuperadmin() || this.user.isLearningExperienceAdministrator()) {
          this.switchView('admin')
        } else {
          this.switchView('learner')
        }
      } else {
        if (this.routeQueryView === 'course-creator' && !this.user.isCourseCreator()) {
          this.switchView('admin')
        }
        if (this.routeQueryView === 'admin' && !this.user.isSuperadmin() && !this.user.isLearningExperienceAdministrator()) {
          this.switchView('learner')
        }
      }
    },
    handleEmitRequestFinally () {
      this.$eventBus.$emit('course-list-ready')
    },
    handleListExtraFilters (data) {
      setTimeout(() => {
        this.$refs.componentlist.getItems()
      }, 100)
    }
  },
  created () {
    this.initializeRouteQuery()
    if (this.routeModulesQuery) {
      this.handleListExtraFilters()
    }
  },
  updated () {
    this.initializeRouteQuery()
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courselist";
</style>
